import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { useAutoSave } from '@/hooks/useAutoSave';
import { DndProvider } from '@/components/email-builder/DndProvider';
import { BuilderToolbar } from '@/components/email-builder/BuilderToolbar';
import { Canvas } from '@/components/email-builder/Canvas';
import { BlockPalette } from '@/components/email-builder/BlockPalette';
import { PropertiesPanel } from '@/components/email-builder/PropertiesPanel';
// Import blocks to ensure registration happens
import '@/components/email-builder/blocks';
import { flushPendingEdits } from '@/lib/email-builder/pending-edits';

export default function EmailBuilderPage() {
  const params = useParams();
  const loadDocument = useEmailBuilderStore((s) => s.loadDocument);
  const createDocument = useEmailBuilderStore((s) => s.createDocument);
  const reset = useEmailBuilderStore((s) => s.reset);

  // Enable auto-save with 30-second interval
  // Creates checkpoints every 5 auto-saves for version history
  useAutoSave({ 
    interval: 30000, // 30 seconds
    checkpointInterval: 5, // Checkpoint every 5 auto-saves (2.5 minutes)
    enabled: true 
  });

  useEffect(() => {
    const documentId = params.id as string;

    if (documentId === 'new') {
      // Create new document
      createDocument('Untitled Email').catch((error) => {
        console.error('Failed to create document:', error);
      });
    } else {
      // Load existing document
      loadDocument(documentId).catch((error) => {
        console.error('Failed to load document:', error);
      });
    }

    return () => {
      // Send debounced edits while the document is still loaded; child components' own unmount
      // flushes may run after reset() and would find no document.
      flushPendingEdits();
      reset(); // Cleanup on unmount
    };
  }, [params.id, loadDocument, createDocument, reset]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + S = Save, from anywhere in the editor (including form fields), instead of the
      // browser's "Save page" dialog. Text blocks handle it themselves first.
      if (modifier && e.key === 's') {
        e.preventDefault();
        useEmailBuilderStore.getState().saveDocument().catch(() => {
          // saveError is shown in the toolbar
        });
        return;
      }

      // Leave other keys to form fields and text being edited (native undo, Backspace, ...)
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        return;
      }

      // Cmd/Ctrl + Z = Undo
      if (modifier && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        useEmailBuilderStore.getState().undo();
      }

      // Cmd/Ctrl + Shift + Z = Redo
      if (modifier && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        useEmailBuilderStore.getState().redo();
      }

      // Escape = Deselect block (back to document settings)
      if (e.key === 'Escape') {
        const { selectedBlockId, selectBlock } = useEmailBuilderStore.getState();
        if (selectedBlockId) {
          e.preventDefault();
          selectBlock(null);
        }
      }

      // Delete/Backspace = Delete selected block (but not when editing text)
      if ((e.key === 'Delete' || e.key === 'Backspace') && !modifier) {
        // Don't delete the block if the user is editing inside a contentEditable element
        const active = document.activeElement as HTMLElement | null;
        if (active?.isContentEditable) return;

        const { selectedBlockId, deleteBlockAction } = useEmailBuilderStore.getState();
        if (selectedBlockId) {
          e.preventDefault();
          deleteBlockAction(selectedBlockId);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <DndProvider>
      <div className="lw-email-builder-page flex h-[90vh] flex-col bg-background">
        {/* Toolbar */}
        <BuilderToolbar />

        {/* Main content */}
        <div className="lw-email-builder-layout flex flex-1 overflow-hidden">
          {/* Left: Block Palette - Hidden on mobile, shown on lg+ */}
          <aside className="lw-email-builder-palette-panel hidden lg:block lg:w-64 border-r border-border bg-card overflow-hidden">
            <BlockPalette />
          </aside>

          {/* Center: Canvas */}
          <main className="lw-email-builder-canvas-panel flex-1 overflow-hidden">
            <Canvas />
          </main>

          {/* Right: Properties Panel - Hidden on mobile, shown on xl+ */}
          <aside className="lw-email-builder-properties-panel hidden xl:block xl:w-72 border-l border-border bg-card overflow-hidden">
            <PropertiesPanel />
          </aside>
        </div>
      </div>
    </DndProvider>
  );
}
